import React, { useCallback, useEffect, useState } from 'react';
import { Copy, KeyRound, Ban, Plus, Trash2 } from 'lucide-react';
import { adminApi, type ApiPlan, type ApiTrialOffer } from '../../api';
import { formatPlanPrice, getPricingOptions } from '../../lib/planPricing';
import { SectionHeader, confirmPermanentDelete } from '../shared';
import type { SuperAdminContext } from '../types';

export function TrialsSection({ ctx }: { ctx: SuperAdminContext }) {
  const [trials, setTrials] = useState<ApiTrialOffer[]>([]);
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [planId, setPlanId] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([adminApi.listTrials(), adminApi.listPlans()]);
      setTrials(tRes.trials);
      setPlans(pRes.plans.filter((p) => p.isActive));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load trials.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createTrial = async () => {
    if (!label.trim() || !planId || !validFrom || !validUntil) {
      setError('Label, plan, and date range are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await adminApi.createTrial({
        label: label.trim(),
        planId,
        validFrom: new Date(validFrom).toISOString(),
        validUntil: new Date(validUntil).toISOString(),
        maxRedemptions: maxRedemptions.trim() ? Number(maxRedemptions) : null,
      });
      setShowForm(false);
      setLabel('');
      setPlanId('');
      setValidFrom('');
      setValidUntil('');
      setMaxRedemptions('');
      await load();
      window.alert(`Trial created.\n\n6-character code (share with shop):\n${res.trial.secretKey}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create trial.');
    } finally {
      setSaving(false);
    }
  };

  const cancelTrial = async (trial: ApiTrialOffer) => {
    if (!window.confirm(`Cancel trial "${trial.label}"? The secret key will stop working for new signups.`)) return;
    try {
      await adminApi.cancelTrial(trial.id);
      await load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to cancel trial.');
    }
  };

  const removeTrial = async (trial: ApiTrialOffer) => {
    if (!confirmPermanentDelete('trial key', trial.label)) return;
    try {
      await adminApi.deleteTrial(trial.id);
      await load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to delete trial key.');
    }
  };

  const cancelOrgTrial = async (orgId: string, orgName: string) => {
    if (!window.confirm(`End free trial for "${orgName}" immediately?`)) return;
    try {
      await adminApi.cancelOrgTrial(orgId);
      await ctx.reload();
      await load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to cancel org trial.');
    }
  };

  const trialOrgs = ctx.orgs.filter((o) => o.subscriptionSource === 'trial' && o.subscriptionStatus === 'active');

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Free trial offers"
        subtitle="Create calendar-based 6-character trial codes. Shops enter the code at registration; the assigned plan applies to the same workspace database."
        actions={
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
            Create trial
          </button>
        }
      />

      {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-900">{error}</div> : null}

      {showForm ? (
        <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-zinc-900">New trial offer</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block text-sm sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Label (internal)</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Hyderabad roadshow July" className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Plan for trial</span>
              <select value={planId} onChange={(e) => setPlanId(e.target.value)} className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm">
                <option value="">Select plan…</option>
                {plans.map((p) => {
                  const pricing = getPricingOptions(p)
                    .map((o) => `${o.label}: ${formatPlanPrice(o.priceInr)}`)
                    .join(' · ');
                  return (
                    <option key={p.planId} value={p.planId}>
                      {p.name} ({pricing || 'no pricing'})
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Valid from</span>
              <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="mt-1 block w-full min-w-0 max-w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Valid until</span>
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1 block w-full min-w-0 max-w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Max redemptions (optional)</span>
              <input type="number" min={1} value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} placeholder="Unlimited if empty" className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
            </label>
          </div>
          <p className="mt-3 text-xs text-zinc-500">
            A unique 6-character code is generated when you save. It appears in Trial keys below — share it with the shop for registration.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-700">
              Cancel
            </button>
            <button type="button" disabled={saving} onClick={() => void createTrial()} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? 'Creating…' : 'Generate 6-character code'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="border-b border-zinc-100 px-4 py-3">
          <p className="text-sm font-semibold text-zinc-900">Trial keys</p>
        </div>
        {loading ? (
          <p className="p-4 text-sm text-zinc-500">Loading…</p>
        ) : trials.length === 0 ? (
          <p className="p-4 text-sm text-zinc-500">No trial offers yet.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {trials.map((t) => (
              <li key={t.id} className="px-4 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-zinc-900">{t.label}</p>
                    <p className="text-xs text-zinc-500">
                      Plan: {t.planId} · {new Date(t.validFrom).toLocaleDateString()} → {new Date(t.validUntil).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-zinc-500">
                      Redeemed {t.redemptionCount}
                      {t.maxRedemptions != null ? ` / ${t.maxRedemptions}` : ''}
                    </p>
                    <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-1.5 font-mono text-sm font-bold tracking-[0.2em] text-emerald-900">
                      <KeyRound className="h-4 w-4 text-emerald-600" />
                      {t.secretKey || t.secretKeyHint || '—'}
                      {t.secretKey ? (
                        <button
                          type="button"
                          title="Copy code"
                          onClick={() => void navigator.clipboard.writeText(t.secretKey || '')}
                          className="rounded p-1 hover:bg-emerald-100"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {!t.isActive || t.cancelledAt ? (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">Cancelled</span>
                    ) : (
                      <button type="button" onClick={() => void cancelTrial(t)} className="inline-flex items-center gap-1 rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50">
                        <Ban className="h-3.5 w-3.5" />
                        Cancel key
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void removeTrial(t)}
                      className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
        <h3 className="text-sm font-semibold text-zinc-900">Active trial tenants</h3>
        {trialOrgs.length === 0 ? (
          <p className="mt-2 text-sm text-zinc-600">No organizations currently on a trial.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {trialOrgs.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-sm">
                <div>
                  <p className="font-medium text-zinc-900">{o.name}</p>
                  <p className="text-xs text-zinc-500">
                    {o.planId} · {o.subscriptionExpiresAt ? `ends ${new Date(o.subscriptionExpiresAt).toLocaleDateString()}` : 'no expiry'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => void cancelOrgTrial(o.id, o.name)} className="text-xs font-semibold text-amber-800 hover:underline">
                    End trial
                  </button>
                  <button type="button" onClick={() => void ctx.onDeleteOrg(o)} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:underline">
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete tenant
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
